import { useState, useEffect, useContext } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { LocaleContext } from "../../../routing/LangRouter";

//import general components
import { BaseButton } from "../base-button/base-button";
import BasePopover from "../base-popover/base-popover";
import BaseTextField from "../base-text-field/base-text-field";

//import images and icons
import { Profile2User } from "iconsax-react";

//import styles
import "./person-count.scss";

function PersonCount({ list, handleChange, defaultValue }) {
    //general variables
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [parent] = useAutoAnimate();

    const [personCounts, setPersonCounts] = useState({ adult: 1, child: 0, infant: 0, totalPersons: 1 });

    //update the count of persons based on the action type (increase or decrease)
    const updatePersonCount = (event, actionType, validation) => {
        event.preventDefault();
        const { name, value } = event.target;

        if (actionType === "increase") {
            increasePersonCount(name, value, validation);
        }

        if (actionType === "decrease") {
            decreasePersonCount(name, value, validation);
        }
    };

    //increases the count of a specific person type (adult, child, or infant)
    const increasePersonCount = (name, value, validation) => {
        const { adult, child, infant, totalPersons } = personCounts;
        if (validation) {
            if (name === "infant" && infant >= adult) {
                return toast.warning(t("personCountMessage2"), { toastId: "custom-id-yes" });
            }
            if (adult + child >= 9 && name !== "infant") {
                return toast.warning(t("personCountMessage1"), { toastId: "custom-id-yes" });
            }
            if (name !== "adult" && (infant + child) / 3 >= adult) {
                return toast.warning(t("personCountMessage3"), { toastId: "custom-id-yes" });
            }
        }
        setPersonCounts(prevCounts => ({
            ...prevCounts,
            [name]: parseInt(value) + 1,
            totalPersons: totalPersons + 1,
        }));
    };

    //decrease the count of a specific person type (adult, child, or infant)
    const decreasePersonCount = (name, value, validation) => {
        const { adult, child, infant, totalPersons } = personCounts;
        if (validation) {
            if (name === "adult" && (value <= 1 || infant >= value)) {
                return;
            }
            if (name === "adult" && (infant + child) / 3 >= adult - 1) {
                return toast.warning(t("personCountMessage3"), { toastId: "custom-id-yes" });
            }
            if ((name === "child" || name === "infant") && value <= 0) {
                return;
            }
        }
        setPersonCounts(prevCounts => ({
            ...prevCounts,
            [name]: Math.max(0, parseInt(value) - 1),
            totalPersons: totalPersons - 1,
        }));
    };

    //updated to use personCounts instead of counter
    useEffect(() => {
        handleChange(personCounts);
    }, [personCounts]);

    //set default value for person counts
    useEffect(() => {
        if (defaultValue) {
            setPersonCounts(values => ({ ...values, ...defaultValue }));
        }
    }, [defaultValue]);

    //Popover
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
        <Grid className="person-count" container item xs={12} order={{ xs: 2, md: 1 }}>
            <BaseTextField
                aria-describedby={id}
                onClick={handleClick}
                label={t("passengers")}
                value={`${personCounts?.totalPersons} ${t("traveler")}`}
                radius="10px"
                readOnly={true}
                icon={<Profile2User color="var(--teal-color-18)" variant="Outline" />}
            />

            <BasePopover
                id={id}
                open={open}
                anchorEl={anchorEl}
                handleClose={handleClose}
                width="16%"
                vertical="bottom"
                horizontal={locale === "en" ? "left" : "right"}
            >
                <Grid container item xs={12} rowGap={{ xs: 1, md: 2 }} alignItems="flex-end">
                    {list?.map(person => {
                        return (
                            <Grid
                                className="person-count__item"
                                key={person?.id}
                                container
                                item
                                xs={12}
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid container item xs={6} direction="column">
                                    <span className="person-count__title">{person.label}</span>
                                    <span className="person-count__age-range">{person.description}</span>
                                </Grid>
                                <Grid container item xs={6} justifyContent="space-between" alignItems="center">
                                    <Grid container item xs={3} justifyContent="center" order={locale === "en" ? 3 : 1}>
                                        <button
                                            className="person-count__button-counter"
                                            name={person.name}
                                            value={personCounts[person.name]}
                                            onClick={e => updatePersonCount(e, "increase", person.validation)}
                                        >
                                            +
                                        </button>
                                    </Grid>
                                    <Grid container item xs={6} justifyContent="center" order={2}>
                                        <span className="person-count__label-counter">{personCounts[person.name]}</span>
                                    </Grid>
                                    <Grid container item xs={3} justifyContent="center" order={locale === "en" ? 1 : 3}>
                                        <button
                                            className={`person-count__button-counter ${personCounts?.adult === 1 ? "disable" : ""}`}
                                            name={person.name}
                                            value={personCounts[person.name]}
                                            onClick={e => updatePersonCount(e, "decrease", person.validation)}
                                            disabled={person.name === "adult" && person.value === 1 ? true : person.value === 0 ? true : false}
                                        >
                                            -
                                        </button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        );
                    })}
                    <Grid container item xs={12} display={{ xs: "flex", md: "none" }}>
                        <BaseButton text={t("confirmation")} color="primary" size="medium" radius="radius-10" handleClick={handleClose} />
                    </Grid>
                </Grid>
            </BasePopover>
        </Grid>
    );
}

export default PersonCount;
